// Signature-block image upload helpers (private bucket: composer-block-images).
//
// Bucket flipped from public → private on 2026-05-27 (security finding):
// "composer-block-images is a public bucket writable by any workspace member".
// Reads now go through signed URLs scoped to workspace membership.
//
// NOTE on publishing: signed URLs embedded into outbound published HTML do not
// solve the deeper "Excerptus shouldn't be the storehouse for image hot-links"
// issue — that requires per-adapter media handoff (upload to WordPress media
// library, Ghost API images, etc.). See backlog:
// mem://backlog/publishing-media-handoff.md.

import { supabase } from "@/integrations/supabase/client";

export const BLOCK_IMAGES_BUCKET = "composer-block-images";
export const BLOCK_IMAGE_MAX_BYTES = 2 * 1024 * 1024; // 2 MB
export const BLOCK_IMAGE_ALLOWED_TYPES: ReadonlySet<string> = new Set([
  "image/png",
  "image/jpeg",
  "image/webp",
]);

/** 7 days — matches `brief-images` pattern; callers should refresh on each editor session. */
// 1-hour signed URLs (Stage 8 of the publishing media-handoff plan).
// Pending-media checklist regenerates URLs on click, so short TTL never blocks users.
const SIGNED_URL_TTL_SECONDS = 60 * 60;

const blockImageExt = (mime: string): string => {
  switch (mime) {
    case "image/png": return "png";
    case "image/jpeg": return "jpg";
    case "image/webp": return "webp";
    default: return "bin";
  }
};

const blockImageRandomId = (): string => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export type UploadedBlockImage = { path: string; signedUrl: string };

export const uploadBlockImage = async (
  workspaceId: string,
  blockId: string,
  file: File,
): Promise<UploadedBlockImage> => {
  if (!BLOCK_IMAGE_ALLOWED_TYPES.has(file.type)) {
    throw new Error(`Unsupported image type: ${file.type || "unknown"}. Use PNG, JPEG, or WebP.`);
  }
  if (file.size > BLOCK_IMAGE_MAX_BYTES) {
    throw new Error(`Image is too large (max ${BLOCK_IMAGE_MAX_BYTES / (1024 * 1024)} MB).`);
  }
  const path = `${workspaceId}/${blockId}/${blockImageRandomId()}.${blockImageExt(file.type)}`;
  const { error } = await supabase.storage
    .from(BLOCK_IMAGES_BUCKET)
    .upload(path, file, { contentType: file.type, upsert: false });
  if (error) throw error;

  const signedUrl = await refreshBlockImageUrl(path);
  if (!signedUrl) throw new Error("Failed to sign block image URL");
  return { path, signedUrl };
};

/** Refresh a signed URL for an existing path (call after listing blocks). */
export const refreshBlockImageUrl = async (path: string): Promise<string | null> => {
  if (!path) return null;
  const { data, error } = await supabase.storage
    .from(BLOCK_IMAGES_BUCKET)
    .createSignedUrl(path, SIGNED_URL_TTL_SECONDS);
  if (error || !data) return null;
  return data.signedUrl;
};

export const deleteBlockImageByPath = async (path: string): Promise<void> => {
  if (!path) return;
  await supabase.storage.from(BLOCK_IMAGES_BUCKET).remove([path]);
};
